import React from 'react';
import { Box, Container } from '@mui/material';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';

const PageSection = styled(Box)`
  min-height: 100vh;
  padding: 120px 0 80px;
  background: linear-gradient(135deg, #000B18 0%, #001529 100%);
  color: #fff;
`;

const BackgroundPattern = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    linear-gradient(90deg, rgba(0, 229, 255, 0.03) 1px, transparent 1px),
    linear-gradient(0deg, rgba(0, 229, 255, 0.03) 1px, transparent 1px);
  background-size: 40px 40px;
  opacity: 0.2;
  z-index: 1;
`;

const Content = styled(Container)`
  position: relative;
  z-index: 2;
`;

const Title = styled(motion.h1)`
  font-size: 3.5rem;
  font-weight: 700;
  margin-bottom: 2rem;
  background: linear-gradient(90deg, #4facfe 0%, #00f2fe 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-align: center;
`;

const Section = styled.div`
  margin-bottom: 3rem;
  background: rgba(10, 25, 41, 0.5);
  padding: 2rem;
  border-radius: 16px;
  border: 1px solid rgba(79, 172, 254, 0.2);
  backdrop-filter: blur(10px);

  h2 {
    font-size: 2rem;
    color: #4facfe;
    margin-bottom: 1.5rem;
    font-weight: 600;
  }

  h3 {
    font-size: 1.5rem;
    color: #4facfe;
    margin: 2rem 0 1rem;
    font-weight: 500;
  }

  p {
    color: rgba(255, 255, 255, 0.8);
    font-size: 1.1rem;
    line-height: 1.8;
    margin-bottom: 1.5rem;
  }

  ul {
    color: rgba(255, 255, 255, 0.8);
    font-size: 1.1rem;
    line-height: 1.8;
    margin-bottom: 1.5rem;
    padding-left: 2rem;

    li {
      margin-bottom: 1rem;
    }
  }
`;

const EffectiveDate = styled.p`
  text-align: center;
  color: rgba(255, 255, 255, 0.7);
  font-size: 1.1rem;
  margin: -1rem 0 3rem;
`;

const ContactInfo = styled.div`
  margin-top: 2rem;
  padding: 2rem;
  background: rgba(79, 172, 254, 0.05);
  border-radius: 16px;
  border: 1px solid rgba(79, 172, 254, 0.2);

  p {
    margin-bottom: 0.5rem;
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const LastUpdated = styled.p`
  text-align: center;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.9rem;
  margin-top: 4rem;
`;

const PrivacyPolicy = () => {
  return (
    <>
      <Navbar />
      <PageSection>
        <BackgroundPattern />
        <Content maxWidth="lg">
          <Title
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Privacy Policy
          </Title>
          <EffectiveDate>Effective Date: April 2025</EffectiveDate>

          <Section>
            <h2>1. INTRODUCTION</h2>
            <p>
              At Yurix Ventures Private Limited ("Yurix," "we," "us," or "our"), we are committed to protecting your privacy. This Privacy Policy explains how we collect, use, store, protect, and share your personal information when you visit our website, use our services, or interact with us in any way.
            </p>
            <p>
              This Privacy Policy applies to all services offered by Yurix, including AI and data science consulting, machine learning solutions, data analytics, and digital transformation services.
            </p>
            <p>
              By accessing or using our services, you acknowledge that you have read, understood, and agree to the practices described in this Privacy Policy.
            </p>
          </Section>

          <Section>
            <h2>2. INFORMATION WE COLLECT</h2>
            <h3>2.1 Information You Provide to Us</h3>
            <p>We collect information you provide directly to us, including:</p>
            <ul>
              <li>Personal identification information (name, email address, phone number)</li>
              <li>Business information (company name, job title, industry)</li>
              <li>Payment information when you purchase our services</li>
              <li>Content of communications with us, including support requests, feedback, and testimonials</li>
              <li>Information shared during consultations, project scoping, and service delivery</li>
              <li>Marketing preferences and survey responses</li>
            </ul>

            <h3>2.2 Information We Collect Automatically</h3>
            <p>When you interact with our website and services, we may automatically collect:</p>
            <ul>
              <li>Technical information about your device (device type, operating system, browser type)</li>
              <li>Internet connection information (IP address, access times, pages viewed)</li>
              <li>Usage data (features used, interactions with our interfaces)</li>
              <li>Cookies and similar tracking technologies (see our Cookie Policy for details)</li>
            </ul>

            <h3>2.3 Information from Third Parties</h3>
            <p>We may receive information about you from:</p>
            <ul>
              <li>Business partners with whom we offer co-branded services</li>
              <li>Service providers who help us deliver our services</li>
              <li>Publicly available sources, such as professional networks</li>
            </ul>
          </Section>

          <Section>
            <h2>3. HOW WE USE YOUR INFORMATION</h2>
            <p>We use your information for the following purposes:</p>
            
            <h3>3.1 Service Provision and Improvement</h3>
            <ul>
              <li>Provide, maintain, and enhance our services</li>
              <li>Process and complete transactions</li>
              <li>Personalize your experience based on your preferences</li>
              <li>Develop new features, products, and services</li>
            </ul>

            <h3>3.2 Communication</h3>
            <ul>
              <li>Send service notifications and support messages</li>
              <li>Respond to your inquiries and comments</li>
              <li>Provide information about our services, events, and promotions</li>
              <li>Deliver important updates about our terms, policies, and practices</li>
            </ul>

            <h3>3.3 Analysis and Research</h3>
            <ul>
              <li>Understand how users access and use our services</li>
              <li>Conduct market research and analysis</li>
              <li>Monitor and analyze trends, usage, and activities</li>
              <li>Evaluate and improve the effectiveness of our services</li>
            </ul>

            <h3>3.4 Legal and Safety</h3>
            <ul>
              <li>Detect, investigate, and prevent fraudulent transactions and other illegal activities</li>
              <li>Protect the rights, property, or safety of Yurix, our users, or others</li>
              <li>Comply with applicable laws, regulations, and legal processes</li>
            </ul>
          </Section>

          <Section>
            <h2>4. HOW WE SHARE YOUR INFORMATION</h2>
            <p>We may share your information in the following circumstances:</p>

            <h3>4.1 With Service Providers</h3>
            <p>
              We share information with third-party vendors, consultants, and service providers who perform services on our behalf, such as payment processing, data analytics, email delivery, and customer service.
            </p>

            <h3>4.2 For Business Transfers</h3>
            <p>
              If Yurix is involved in a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction. We will notify you of any change in ownership or uses of your information.
            </p>

            <h3>4.3 For Legal Reasons</h3>
            <p>We may disclose your information if we believe it is necessary to:</p>
            <ul>
              <li>Comply with applicable laws, regulations, legal processes, or governmental requests</li>
              <li>Enforce our Terms of Service and other agreements</li>
              <li>Detect, investigate, and address fraud, security, or technical issues</li>
              <li>Protect the rights, property, or safety of Yurix, our users, or others</li>
            </ul>

            <h3>4.4 With Your Consent</h3>
            <p>
              We may share your information with third parties when you have given us your consent to do so.
            </p>
          </Section>

          <Section>
            <h2>5. DATA SECURITY</h2>
            <p>
              We implement appropriate technical, administrative, and physical security measures to protect your information from unauthorized access, disclosure, alteration, and destruction. Our security practices include:
            </p>
            <ul>
              <li>Encryption of sensitive data in transit and at rest</li>
              <li>Regular security assessments and penetration testing</li>
              <li>Access controls and authentication mechanisms</li>
              <li>Employee training on data protection and security</li>
            </ul>
            <p>
              However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your information, we cannot guarantee its absolute security.
            </p>
          </Section>

          <Section>
            <h2>6. DATA RETENTION</h2>
            <p>
              We retain your information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. When determining how long to keep your data, we consider:
            </p>
            <ul>
              <li>The nature of the information and its sensitivity</li>
              <li>The potential risk of harm from unauthorized use or disclosure</li>
              <li>The purposes for which we process the information</li>
              <li>Whether we can achieve those purposes through other means</li>
              <li>Applicable legal requirements</li>
            </ul>
          </Section>

          <Section>
            <h2>7. YOUR RIGHTS AND CHOICES</h2>
            <p>Depending on your location, you may have various rights regarding your personal information, including:</p>

            <h3>7.1 Access and Portability</h3>
            <p>You have the right to access and receive a copy of the personal information we hold about you.</p>

            <h3>7.2 Correction</h3>
            <p>You have the right to request that we correct inaccurate or incomplete information about you.</p>

            <h3>7.3 Deletion</h3>
            <p>You have the right to request the deletion of your personal information, subject to certain exceptions.</p>

            <h3>7.4 Restriction and Objection</h3>
            <p>You have the right to request that we restrict or cease processing your information for specific purposes and to object to processing in certain circumstances.</p>

            <h3>7.5 Withdrawal of Consent</h3>
            <p>Where we rely on your consent to process your information, you have the right to withdraw that consent at any time.</p>

            <h3>7.6 Complaint</h3>
            <p>You have the right to lodge a complaint with a supervisory authority if you believe our processing of your information violates applicable law.</p>
            <p>To exercise any of these rights, please contact us using the details provided in the "Contact Us" section below.</p>
          </Section>

          <Section>
            <h2>8. INTERNATIONAL DATA TRANSFERS</h2>
            <p>
              Yurix is based in Lucknow, Uttar Pradesh, India, and your information may be processed and stored in India and other countries. These countries may have data protection laws different from those in your country of residence. By using our services, you consent to the transfer of your information to India and other countries where Yurix operates.
            </p>
          </Section>

          <Section>
            <h2>9. CHILDREN'S PRIVACY</h2>
            <p>
              Our services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately.
            </p>
          </Section>

          <Section>
            <h2>10. CHANGES TO THIS PRIVACY POLICY</h2>
            <p>
              We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. We will notify you of any material changes by posting the updated Privacy Policy on our website and changing the "Effective Date" at the top of this policy. We encourage you to review this Privacy Policy periodically.
            </p>
          </Section>

          <Section>
            <h2>11. CONTACT US</h2>
            <p>If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us at:</p>
            <ContactInfo>
              <p>Yurix Ventures Private Limited</p>
              <p>Lucknow, Uttar Pradesh, India</p>
              <p>Email: yurixventures@gmail.com</p>
              <p>Phone: +91 8756992565</p>
            </ContactInfo>
          </Section>

          <LastUpdated>Last Updated: April 2025</LastUpdated>
        </Content>
      </PageSection>
    </>
  );
};

export default PrivacyPolicy; 